import { NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Users, ListTodo, BarChart3, Bot, GitBranch,
  Printer, PieChart, Receipt, UserCog, Tag, Activity, Settings, MessageSquare, FileText, Monitor, Calculator, Boxes, Wand2, Package, Trophy, Plane, ShoppingCart, Sparkles, Layers, UserPlus
} from 'lucide-react';
import { loadPermissions, type Permissions } from '../store/permissions';

interface NavItem { to: string; icon: typeof LayoutDashboard; label: string; module: string; external?: boolean; }

const agentItems: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'agentes' },
  { to: '/agents', icon: Users, label: 'Agentes', module: 'agentes' },
  { to: '/tasks', icon: ListTodo, label: 'Tareas', module: 'tareas' },
  { to: '/reporting', icon: BarChart3, label: 'Reportería', module: 'reporting' },
  { to: '/diagrama', icon: GitBranch, label: 'Flujo de Agentes', module: 'diagrama' },
  { to: '/chat', icon: MessageSquare, label: 'Chat IA', module: 'agentes' },
];

const dtfItems: NavItem[] = [
  { to: '/dtf/dashboard', icon: PieChart, label: 'Dashboard DTF', module: 'dashboard' },
  { to: '/dtf/ordenes', icon: Printer, label: 'Trabajos DTF', module: 'trabajos' },
  { to: '/dtf/cuentas', icon: Receipt, label: 'Cuentas por Cobrar', module: 'cuentas' },
  { to: '/dtf/clientes', icon: UserCog, label: 'Clientes', module: 'clientes' },
  { to: '/dtf/etiquetas', icon: Tag, label: 'Mensajería', module: 'mensajeria' },
  { to: '/dtf/montaje', icon: Wand2, label: 'Montaje TPU', module: 'montaje-tpu' },
  { to: '/dtf/ai-studio', icon: Sparkles, label: 'AI Studio', module: 'montaje-tpu' },
  { to: '/dtf/wizard-users', icon: UserPlus, label: 'Wizard Clientes', module: 'wizard-logos' },
  { to: '/dtf/cola-tpu', icon: ListTodo, label: 'Cola TPU', module: 'cola-tpu' },
  { to: '/dtf/wa-log', icon: MessageSquare, label: 'WhatsApp Log', module: 'wa-log' },
  { to: '/dtf/aura-screen', icon: Monitor, label: 'Pantalla Tienda', module: 'aura-screen' },
  { to: '/subli-v3', icon: Sparkles, label: 'Sublimación CAD', module: 'sublimacion' },
];

function NavSection({ title, items }: { title: string; items: NavItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">{title}</p>
      <div className="space-y-0.5">
        {items.map(({ to, icon: Icon, label, external }) => (
          external ? (
            <a key={to} href={to} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 text-gray-400 hover:bg-gray-800 hover:text-white"
            >
              <Icon size={17} />
              {label}
            </a>
          ) : (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon size={17} />
              {label}
            </NavLink>
          )
        ))}
      </div>
    </div>
  );
}

export default function Sidebar() {
  const [perms, setPerms] = useState<Permissions | null>(null);

  useEffect(() => {
    loadPermissions().then(setPerms);
  }, []);

  // Filtrar items según permisos
  const hasModule = (mod: string) => {
    if (!perms) return false;
    return perms.modules.includes(mod) || perms.modules.some(m => m.startsWith(mod) || mod.startsWith(m.split(':')[0]));
  };

  const filteredAgents = perms?.showAgentesIA ? agentItems : [];
  const filteredDtf = dtfItems.filter(item => item.module === 'cola-tpu' ? !!(perms as { canAddColaTpu?: boolean } | null)?.canAddColaTpu : hasModule(item.module));

  // Admin items
  const showAdmin = perms?.canManageUsers;

  return (
    <aside className="w-full lg:w-56 h-full bg-gray-900 flex flex-col flex-shrink-0 overflow-hidden">
      {/* Logo */}
      <div className="p-5 border-b border-gray-700">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Bot size={18} className="text-white" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm leading-none">DTFCR</p>
            <p className="text-gray-400 text-xs mt-0.5">{perms?.role || '...'}</p>
          </div>
        </div>
      </div>

      {/* Navegación */}
      <nav className="flex-1 p-3 space-y-5 overflow-y-auto scrollbar-thin">
        {filteredAgents.length > 0 && <NavSection title="Agentes IA" items={filteredAgents} />}
        {filteredDtf.length > 0 && <NavSection title="Producción DTF" items={filteredDtf} />}
        {(hasModule('augustito') || hasModule('centro-costos') || hasModule('inventario')) && (
          <div>
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">Finanzas</p>
            {hasModule('augustito') && (
              <NavLink to="/augustito" className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive ? 'bg-green-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}>
                <Calculator size={17} /> Augustito
              </NavLink>
            )}
            {hasModule('centro-costos') && (
              <NavLink to="/centro-costos" className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 mt-0.5 ${
                  isActive ? 'bg-green-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}>
                <Calculator size={17} /> Centro de Costos
              </NavLink>
            )}
            {hasModule('inventario') && (
              <NavLink to="/dtf/inventario" className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 mt-0.5 ${
                  isActive ? 'bg-green-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}>
                <Boxes size={17} /> Inventario
              </NavLink>
            )}
          </div>
        )}
        {hasModule('logistica') && (
          <div>
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">Logística</p>
            <NavLink to="/logistica" className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive ? 'bg-orange-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}>
              <Package size={17} /> Marta
            </NavLink>
          </div>
        )}
        {perms?.role === 'master' && (
          <div>
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-500">Q&amp;A (pruebas)</p>
            <NavLink to="/caja-qa" className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive ? 'bg-amber-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}>
              <span className="w-[17px] text-center">💰</span> Caja (Q&amp;A)
            </NavLink>
          </div>
        )}
        {showAdmin && (
          <div>
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">Administración</p>
            <NavLink to="/admin/usuarios" className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}>
              <Settings size={17} /> Usuarios
            </NavLink>
            <NavLink to="/admin/facel" className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}>
              <FileText size={17} /> Facel
            </NavLink>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        <p className="text-gray-500 text-xs text-center">v2.0.0 — DTFCR</p>
      </div>
    </aside>
  );
}
